import type { ContentWatchedCommand } from '~/features/content-watched/index.h'
import type { RemoveFromContinueWatchingRoute } from '~/features/remove-from-continue-watching/index.h'
import type { ModuleStructure, Row } from '../components/index.h'
import type { ContinueWatchingStructureItem } from './matches'

import { isType } from 'variant'
import { Ready } from '../components/index.h'
import { toDirty } from '../components/lib'
import {
  matchesContentWatchedCommand,
  matchesContinueWatchingRemoveRoute,
} from './matches'

const isContinueWatchingRow = (comp: {
  desc: { localizedTitleKey?: string; contentType?: string }
}) =>
  comp.desc.localizedTitleKey === 'continue_watching' &&
  comp.desc.contentType === 'MIXED'

const getReadyContinueWatchingPayload = (
  comp: Row
): ContinueWatchingStructureItem[] | null => {
  if (!isContinueWatchingRow(comp)) return null
  if (!isType(comp.data, Ready)) return null

  const payload = comp.data.payload
  return Array.isArray(payload)
    ? (payload as ContinueWatchingStructureItem[])
    : null
}

export const removeContinueWatchingItemFromStructure = (
  structure: ModuleStructure,
  route: RemoveFromContinueWatchingRoute
): ModuleStructure => {
  const updated = new Map(structure)

  for (const [id, comp] of structure) {
    const payload = getReadyContinueWatchingPayload(comp)
    if (!payload) continue

    const nextPayload = payload.filter(
      (item) => !matchesContinueWatchingRemoveRoute(item, route)
    )

    if (nextPayload.length === payload.length) continue

    updated.set(id, {
      ...comp,
      data: Ready(nextPayload),
    })
  }

  return updated
}

export const applyContentWatchedToStructure = (
  structure: ModuleStructure,
  command: ContentWatchedCommand
): ModuleStructure => {
  const updated = new Map(structure)
  let changed = false

  for (const [id, comp] of structure) {
    const payload = getReadyContinueWatchingPayload(comp)
    if (!payload) continue

    const nextPayload = payload.map((item) => {
      if (!matchesContentWatchedCommand(item, command)) return item

      changed = true
      return { ...item, watched: command.watched }
    })

    if (nextPayload.some((item, index) => item !== payload[index])) {
      updated.set(id, {
        ...comp,
        data: Ready(nextPayload),
      })
    }
  }

  return changed ? updated : structure
}

export const markContinueWatchingRowDirty = (
  structure: ModuleStructure
): ModuleStructure => {
  const updated = new Map(structure)

  for (const [id, comp] of structure) {
    if (!isContinueWatchingRow(comp)) continue

    updated.set(id, toDirty(comp))
  }

  return updated
}
